// Situação dos boletos de uma UC e dos acordos de parcelamento dela, em frases
// curtas — para quem está na porta do cliente.
//
// Pedido de 30/09/2026: na rota de cobrança, para saber se o boleto já foi pago,
// se a UC já tem acordo, se o termo foi assinado, se a entrada caiu e como estão
// as parcelas, era preciso abrir Faturas e Parcelamentos em outra aba. O card da
// parada passa a dizer tudo isso.
//
// Sem I/O: roda sob `node --test`. Quem lê o banco é `rotaCobranca.ts`.

import {
  derivarStatusBoleto, type DadosStatusBoleto, type StatusBoleto,
} from '../../supabase/functions/_shared/statusBoleto.ts';
import { acordoPendente, acordoVigente, ROTULO_STATUS, type StatusAcordo } from './parcelamentoRegras.ts';
import { diasEntre } from './parcelamentoCalculo.ts';

/** Sentido da cor da etiqueta. A tela traduz para classes. */
export type Tom = 'ok' | 'atencao' | 'perigo' | 'info' | 'neutro';

export interface Etiqueta {
  rotulo: string;
  tom: Tom;
}

const dataBr = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const [a, m, d] = String(iso).slice(0, 10).split('-');
  return d ? `${d}/${m}/${a}` : '';
};

const moeda = (v: unknown) =>
  (Number(v) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/** AAAA-MM-DD no fuso do aparelho. `toISOString` é UTC: das 21h em diante em
 *  Goiás já responde o dia seguinte. */
export const isoLocal = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// ------------------------------------------------------------------ boletos

/** A etiqueta de um status já derivado, com as datas que ele cita. */
export function etiquetaDoStatus(
  status: StatusBoleto,
  datas: { vencimento?: string | null; pagoEm?: string | null; quitadoEm?: string | null },
  hoje: Date,
): Etiqueta {
  switch (status) {
    case 'Pago': {
      const em = dataBr(datas.pagoEm);
      return { rotulo: em ? `Pago em ${em}` : 'Pago', tom: 'ok' };
    }
    case 'Quitado': {
      const em = dataBr(datas.quitadoEm);
      return { rotulo: em ? `Quitado em ${em}` : 'Quitado', tom: 'ok' };
    }
    case 'Parcelado':
      return { rotulo: 'Parcelado', tom: 'info' };
    case 'Cancelado':
      return { rotulo: 'Cancelado', tom: 'neutro' };
    case 'Vencido': {
      const dias = datas.vencimento ? diasEntre(datas.vencimento, isoLocal(hoje)) : 0;
      return { rotulo: dias > 0 ? `Vencido há ${dias} dia${dias > 1 ? 's' : ''}` : 'Vencido', tom: 'perigo' };
    }
    case 'Em aberto':
      return { rotulo: datas.vencimento ? `A vencer em ${dataBr(datas.vencimento)}` : 'Em aberto', tom: 'neutro' };
    default:
      return { rotulo: 'Sem cobrança', tom: 'neutro' };
  }
}

/**
 * A situação de um boleto numa etiqueta. O status sai de `derivarStatusBoleto`,
 * a regra única do sistema — o campo cru está dessincronizado nos dois sentidos.
 */
export function etiquetaBoleto(f: DadosStatusBoleto, hoje: Date): Etiqueta & { status: StatusBoleto } {
  const status = derivarStatusBoleto(f, hoje);
  return {
    status,
    ...etiquetaDoStatus(status, {
      vencimento: f.vencimento_boleto || f.data_vencimento || null,
      pagoEm: f.data_pgto_boleto,
      quitadoEm: f.quitado_em,
    }, hoje),
  };
}

// ------------------------------------------------------------------ acordos

export interface ParcelaLida {
  numero_parcela: number;
  /** 'entrada' (parcela 0) ou 'parcela'. */
  tipo?: string | null;
  valor: number | string | null;
  data_vencimento: string | null;
  /** pendente | provisionado | pago | cancelado */
  status: string | null;
  fatura_id?: string | number | null;
}

/** O que a tela lê de `parcelamentos`. */
export interface AcordoLido {
  id: string;
  status: string;
  created_at?: string | null;
  total_acordo?: number | string | null;
  valor_total?: number | string | null;
  entrada_valor?: number | string | null;
  entrada_vencimento?: string | null;
  entrada_fatura_id?: string | number | null;
  entrada_paga_em?: string | null;
  termo_id_autentique?: string | null;
  termo_url?: string | null;
  termo_enviado_em?: string | null;
  termo_assinado_em?: string | null;
  termo_envio_erro?: string | null;
  expira_em?: string | null;
  cancelado_em?: string | null;
  faturas_origem_snapshot?: Array<{ id: string | number; mes_referencia?: string | null }> | null;
  parcelamento_parcelas?: ParcelaLida[] | null;
}

export type SituacaoParcela = 'paga' | 'vencida' | 'a_vencer' | 'prevista' | 'cancelada';

export interface ParcelaResumida {
  numero: number;
  vencimento: string | null;
  valor: number;
  situacao: SituacaoParcela;
  pagaEm: string | null;
}

export interface ResumoAcordo {
  id: string;
  /** Os 8 primeiros caracteres do id — o "ID da negociação" impresso no termo. */
  codigo: string;
  status: string;
  rotuloStatus: string;
  tom: Tom;
  /** Proposta que ainda não vale: as faturas originais seguem cobráveis. */
  pendente: boolean;
  /** Ainda pede atenção: proposta, ativo ou em atraso. */
  vivo: boolean;
  criadoEm: string | null;
  /** Cancelado ou expirado: quando. */
  encerradoEm: string | null;
  total: number | null;
  termo: Etiqueta;
  entrada: Etiqueta;
  parcelas: Etiqueta & { total: number; pagas: number; vencidas: number; valorVencido: number };
  detalheParcelas: ParcelaResumida[];
}

const TOM_STATUS: Record<string, Tom> = {
  aguardando_aprovacao: 'atencao',
  aguardando_assinatura: 'atencao',
  aguardando_entrada: 'atencao',
  ativo: 'ok',
  em_atraso: 'perigo',
  concluido: 'ok',
  cancelado: 'neutro',
  expirado: 'neutro',
};

const encerrado = (status: string) => status === 'cancelado' || status === 'expirado';

export const codigoAcordo = (id: string) => String(id || '').slice(0, 8).toUpperCase();

const parcelasSemEntrada = (a: AcordoLido) =>
  (a.parcelamento_parcelas || [])
    .filter((p) => (p.tipo ?? 'parcela') !== 'entrada' && Number(p.numero_parcela) > 0)
    .sort((x, y) => Number(x.numero_parcela) - Number(y.numero_parcela));

function etiquetaTermo(a: AcordoLido): Etiqueta {
  if (a.termo_assinado_em) return { rotulo: `Termo assinado em ${dataBr(a.termo_assinado_em)}`, tom: 'ok' };
  if (encerrado(a.status)) return { rotulo: 'Termo não chegou a ser assinado', tom: 'neutro' };
  if (a.status === 'aguardando_aprovacao') return { rotulo: 'O termo só sai depois da aprovação da gestão', tom: 'atencao' };
  if (a.termo_id_autentique || a.termo_url || a.termo_enviado_em) {
    return {
      rotulo: `Termo enviado${a.termo_enviado_em ? ` em ${dataBr(a.termo_enviado_em)}` : ''} — falta o cliente assinar`,
      tom: 'atencao',
    };
  }
  if (a.termo_envio_erro) return { rotulo: 'O termo NÃO foi enviado: o envio deu erro', tom: 'perigo' };
  if (acordoVigente(a.status)) return { rotulo: 'Sem termo digital (acordo antigo ou efetivado na mão)', tom: 'neutro' };
  return { rotulo: 'O termo ainda não foi enviado', tom: 'atencao' };
}

function etiquetaEntrada(a: AcordoLido, fatura: DadosStatusBoleto | undefined, hoje: Date): Etiqueta {
  const valor = Number(a.entrada_valor) || 0;
  if (!valor && a.entrada_fatura_id == null) return { rotulo: 'Acordo sem entrada', tom: 'neutro' };
  const quanto = valor ? `Entrada de ${moeda(valor)}` : 'Entrada';
  const status = fatura ? derivarStatusBoleto(fatura, hoje) : null;

  // Paga: o carimbo do acordo (a varredura grava ao efetivar) OU o boleto. A
  // varredura roda a cada 15 min; nesse meio tempo o boleto já diz a verdade.
  if (a.entrada_paga_em || status === 'Pago' || status === 'Quitado') {
    const em = a.entrada_paga_em || fatura?.data_pgto_boleto || fatura?.quitado_em || null;
    return { rotulo: `${quanto} paga${em ? ` em ${dataBr(em)}` : ''}`, tom: 'ok' };
  }
  if (encerrado(a.status)) return { rotulo: `${quanto} não foi paga`, tom: 'neutro' };

  const venc = a.entrada_vencimento || fatura?.vencimento_boleto || fatura?.data_vencimento || null;
  if (status === 'Vencido') return { rotulo: `${quanto} venceu em ${dataBr(venc)} e não foi paga`, tom: 'perigo' };
  if (status === 'Em aberto') return { rotulo: `${quanto} vence em ${dataBr(venc)} — ainda não paga`, tom: 'atencao' };
  if (status === 'Cancelado') return { rotulo: 'O boleto da entrada foi cancelado', tom: 'perigo' };
  if (acordoVigente(a.status)) return { rotulo: `${quanto} não consta como paga`, tom: 'atencao' };
  if (a.entrada_fatura_id == null) return { rotulo: `${quanto}: a cobrança não foi gerada`, tom: 'perigo' };
  // A fatura da entrada existe mas não foi lida: decide pela data.
  if (venc && venc.slice(0, 10) < isoLocal(hoje)) {
    return { rotulo: `${quanto} venceu em ${dataBr(venc)} e não consta como paga`, tom: 'perigo' };
  }
  return { rotulo: `${quanto}${venc ? ` vence em ${dataBr(venc)}` : ''}`, tom: 'atencao' };
}

function resumirParcelas(
  a: AcordoLido,
  faturas: ReadonlyMap<string, DadosStatusBoleto>,
  hoje: Date,
): { etiqueta: ResumoAcordo['parcelas']; detalhe: ParcelaResumida[] } {
  const hojeIso = isoLocal(hoje);
  const pendente = acordoPendente(a.status);
  const linhas = parcelasSemEntrada(a);

  const detalhe = linhas.map((p): ParcelaResumida => {
    const f = p.fatura_id != null ? faturas.get(String(p.fatura_id)) : undefined;
    const st = f ? derivarStatusBoleto(f, hoje) : null;
    const base = {
      numero: Number(p.numero_parcela), vencimento: p.data_vencimento, valor: Number(p.valor) || 0, pagaEm: null as string | null,
    };
    if (p.status === 'cancelado' || st === 'Cancelado' || encerrado(a.status)) return { ...base, situacao: 'cancelada' };
    // 'pago' é gravado pelo gatilho do banco quando a fatura vira Pago; Quitado
    // não passa por ele, por isso o boleto também decide.
    if (p.status === 'pago' || st === 'Pago' || st === 'Quitado') {
      return { ...base, situacao: 'paga', pagaEm: f?.data_pgto_boleto || f?.quitado_em || null };
    }
    // Proposta: as parcelas só viram boleto na efetivação.
    if (pendente) return { ...base, situacao: 'prevista' };
    // Com boleto vivo, ele decide. Sem boleto (não lido, ou de valor zero, que é
    // "Sem cobrança"), decide a data da parcela — a mesma que a varredura usa
    // para pôr o acordo em atraso. Senão a etiqueta diria "Em atraso" e a linha
    // das parcelas, "a vencer".
    const temBoleto = st === 'Vencido' || st === 'Em aberto';
    const venceuPelaData = !!p.data_vencimento && p.data_vencimento.slice(0, 10) < hojeIso;
    if (temBoleto ? st === 'Vencido' : venceuPelaData) return { ...base, situacao: 'vencida' };
    return { ...base, situacao: 'a_vencer' };
  });

  const validas = detalhe.filter((d) => d.situacao !== 'cancelada');
  const total = validas.length;
  const vazio = { total, pagas: 0, vencidas: 0, valorVencido: 0 };

  if (encerrado(a.status)) return { etiqueta: { rotulo: '', tom: 'neutro', ...vazio }, detalhe };
  if (total === 0) {
    return { etiqueta: { rotulo: 'Sem parcelas: a entrada quita o acordo', tom: 'neutro', ...vazio }, detalhe };
  }
  if (pendente) {
    const primeira = validas[0];
    return {
      etiqueta: {
        rotulo: `${total} parcela${total > 1 ? 's' : ''} de ${moeda(primeira.valor)} a partir de ${dataBr(primeira.vencimento)}`
          + ' — só viram boleto quando o acordo valer',
        tom: 'neutro',
        ...vazio,
      },
      detalhe,
    };
  }

  const pagas = validas.filter((d) => d.situacao === 'paga').length;
  const vencidas = validas.filter((d) => d.situacao === 'vencida');
  const valorVencido = vencidas.reduce((s, d) => s + d.valor, 0);
  const proxima = validas.find((d) => d.situacao === 'a_vencer');
  const partes = [`${pagas} de ${total} parcela${total > 1 ? 's' : ''} paga${pagas === 1 ? '' : 's'}`];
  if (vencidas.length) partes.push(`${vencidas.length} vencida${vencidas.length > 1 ? 's' : ''} (${moeda(valorVencido)})`);
  if (proxima) partes.push(`próxima em ${dataBr(proxima.vencimento)}: ${moeda(proxima.valor)}`);
  return {
    etiqueta: {
      rotulo: partes.join(' · '),
      tom: vencidas.length ? 'perigo' : pagas === total ? 'ok' : 'info',
      total,
      pagas,
      vencidas: vencidas.length,
      valorVencido,
    },
    detalhe,
  };
}

/**
 * Tudo o que o cobrador precisa saber de um acordo, em frases.
 *
 * `faturas` são os boletos da entrada e das parcelas como estão hoje (chave =
 * id). Ausente = não lido; a regra cai no carimbo do acordo e na data.
 */
export function resumirAcordo(
  a: AcordoLido,
  faturas: ReadonlyMap<string, DadosStatusBoleto>,
  hoje: Date,
): ResumoAcordo {
  const pendente = acordoPendente(a.status);
  const total = Number(a.total_acordo ?? a.valor_total);
  const entrada = a.entrada_fatura_id != null ? faturas.get(String(a.entrada_fatura_id)) : undefined;
  const { etiqueta: parcelas, detalhe } = resumirParcelas(a, faturas, hoje);
  return {
    id: a.id,
    codigo: codigoAcordo(a.id),
    status: a.status,
    rotuloStatus: ROTULO_STATUS[a.status as StatusAcordo] ?? a.status,
    tom: TOM_STATUS[a.status] ?? 'neutro',
    pendente,
    vivo: pendente || a.status === 'ativo' || a.status === 'em_atraso',
    criadoEm: a.created_at ?? null,
    encerradoEm: a.status === 'cancelado' ? (a.cancelado_em ?? null) : a.status === 'expirado' ? (a.expira_em ?? null) : null,
    total: Number.isFinite(total) && total > 0 ? total : null,
    termo: etiquetaTermo(a),
    entrada: etiquetaEntrada(a, entrada, hoje),
    parcelas,
    detalheParcelas: detalhe,
  };
}

/** Os que pedem atenção primeiro (atraso, proposta, ativo); depois concluídos;
 *  por último os encerrados. Dentro de cada grupo, o mais novo primeiro. */
export function ordenarAcordos<T extends { status: string; created_at?: string | null }>(acordos: T[]): T[] {
  const peso = (s: string) =>
    s === 'em_atraso' ? 0 : acordoPendente(s) ? 1 : s === 'ativo' ? 2 : s === 'concluido' ? 3 : 4;
  return [...acordos].sort((x, y) =>
    peso(x.status) - peso(y.status) || String(y.created_at || '').localeCompare(String(x.created_at || '')));
}

/**
 * O papel de cada fatura nos acordos vivos: boleto da entrada, parcela n/N, ou
 * fatura antiga que o acordo parcelou (ou vai parcelar, se ainda é proposta).
 *
 * É o que responde, na linha da fatura, "essa já está num acordo?". Acordo
 * cancelado ou expirado não entra: ele não explica mais nada da fatura hoje.
 */
export function papelDasFaturas(acordos: AcordoLido[]): Map<string, Etiqueta> {
  const papel = new Map<string, Etiqueta>();
  const marcar = (id: string | number | null | undefined, e: Etiqueta) => {
    if (id == null) return;
    const chave = String(id);
    if (!papel.has(chave)) papel.set(chave, e);
  };
  for (const a of ordenarAcordos(acordos)) {
    if (encerrado(a.status)) continue;
    const cod = codigoAcordo(a.id);
    marcar(a.entrada_fatura_id, { rotulo: `Entrada do acordo ${cod}`, tom: 'info' });
    const parcelas = parcelasSemEntrada(a);
    for (const p of parcelas) {
      marcar(p.fatura_id, { rotulo: `Parcela ${p.numero_parcela}/${parcelas.length} do acordo ${cod}`, tom: 'info' });
    }
    const pendente = acordoPendente(a.status);
    for (const s of a.faturas_origem_snapshot || []) {
      marcar(s?.id, pendente
        ? { rotulo: `Na proposta de acordo ${cod} (${(ROTULO_STATUS[a.status as StatusAcordo] ?? a.status).toLowerCase()})`, tom: 'atencao' }
        : { rotulo: `Parcelada no acordo ${cod}`, tom: 'info' });
    }
  }
  return papel;
}

const PRIORIDADE_CARD: StatusAcordo[] = [
  'em_atraso', 'aguardando_entrada', 'aguardando_assinatura', 'aguardando_aprovacao', 'ativo',
];
const ROTULO_CARD: Partial<Record<StatusAcordo, string>> = {
  em_atraso: 'Acordo em atraso',
  aguardando_entrada: 'Acordo: falta a entrada',
  aguardando_assinatura: 'Acordo: falta assinar',
  aguardando_aprovacao: 'Acordo: aguarda aprovação',
  ativo: 'Em acordo',
};

/** A etiqueta do card fechado: o acordo vivo que mais pede atenção, ou null. */
export function etiquetaAcordoDaParada(acordos: Array<{ status: string }>): Etiqueta | null {
  for (const st of PRIORIDADE_CARD) {
    if (acordos.some((a) => a.status === st)) return { rotulo: ROTULO_CARD[st]!, tom: TOM_STATUS[st] };
  }
  return null;
}
